/** 4. STEEL CURTAIN — brushed-steel / security-console skin. Read-only "what's stored" summary,
 * a /forget-me wipe, the #340 privacy toggle (tracking_enabled: turn off profile/tracking, keep
 * reviews + moderation), plus profile_private / allow_member_dms / notify_my_reviews toggles. */
import type { Preferences, Profile } from '@pghbites/shared';

type ToggleField = 'tracking_enabled' | 'profile_private' | 'allow_member_dms' | 'notify_my_reviews';

interface SteelProps {
  profile: Profile;
  preferences: Preferences;
  onToggle: (field: ToggleField, value: boolean) => void;
  onForgetMe: () => void;
}

function Toggle({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" className="tgl" aria-pressed={on} onClick={onClick}>
      <span className="box">[{on ? 'x' : ' '}]</span> {label}
    </button>
  );
}

export function SteelCurtainWindow({ profile, preferences, onToggle, onForgetMe }: SteelProps) {
  const stored: string[] = [];
  if (profile.preferred_name) stored.push('name');
  if (profile.zip_code) stored.push('zip');
  if (profile.fav_cuisines.length || profile.dietary_avoidances.length) stored.push('tastes');
  if (profile.sports_teams.length) stored.push('teams');
  const storedLabel = profile.tracking_enabled
    ? (stored.length ? stored.join(', ') : 'nothing yet')
    : 'tracking OFF';

  return (
    <div className="sec">
      <div className="seclabel"><span className="dot">🖥️</span> Steel Curtain watches your account</div>
      <div className="monitor">
        <div className="screen">
          <div className="term">
            <div className="ln"><span className="k">&gt; stored:</span> <span className="v">{storedLabel}</span></div>
            <div className="ln"><span className="k">&gt; visibility:</span> <span className="v">{preferences.profile_private ? 'PRIVATE — you only' : 'visible to members'}</span></div>
            <div className="ln"><span className="k">&gt; wipe everything:</span> <code>/forget-me</code></div>
          </div>

          <div className="toggles">
            <Toggle on={profile.tracking_enabled} label="profile + tracking ON (off keeps reviews + moderation)"
              onClick={() => onToggle('tracking_enabled', !profile.tracking_enabled)} />
            <Toggle on={preferences.profile_private} label="private profile"
              onClick={() => onToggle('profile_private', !preferences.profile_private)} />
            <Toggle on={preferences.allow_member_dms} label="allow member DMs"
              onClick={() => onToggle('allow_member_dms', !preferences.allow_member_dms)} />
            <Toggle on={preferences.notify_my_reviews} label="notify me about my reviews"
              onClick={() => onToggle('notify_my_reviews', !preferences.notify_my_reviews)} />
          </div>

          <button type="button" className="wipe" onClick={onForgetMe}>
            &gt; /forget-me — wipe everything (keeps nothing)
          </button>
        </div>
      </div>
      <div className="hint">tap a switch to toggle →</div>
    </div>
  );
}
